/**
 * Sparkline Component
 *
 * Mini line chart for inline trend visualization.
 * Built with SVG for lightweight rendering (no Recharts dependency).
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React, { useMemo } from 'react';
import { CHART_COLORS } from '../../theme/dashboardTheme';

interface SparklineProps {
  /** Data points to plot */
  data: number[];
  /** Width in pixels (default: 80) */
  width?: number;
  /** Height in pixels (default: 30) */
  height?: number;
  /** Line color (default: first chart color) */
  color?: string;
  /** Stroke width (default: 2) */
  strokeWidth?: number;
  /** Show gradient fill below line */
  showFill?: boolean;
  /** Show dot on last point */
  showLastDot?: boolean;
  /** Custom class name */
  className?: string;
}

const Sparkline: React.FC<SparklineProps> = ({
  data,
  width = 80,
  height = 30,
  color = CHART_COLORS[0],
  strokeWidth = 2,
  showFill = true,
  showLastDot = true,
  className = '',
}) => {
  const path = useMemo(() => {
    if (!data || data.length < 2) return { line: '', fill: '' };

    const padding = strokeWidth;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1; // Avoid division by zero

    const points = data.map((value, index) => ({
      x: padding + (index / (data.length - 1)) * chartWidth,
      y: padding + chartHeight - ((value - min) / range) * chartHeight,
    }));

    // Build line path
    const lineSegments = points.map((p, i) =>
      i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`
    );
    const linePath = lineSegments.join(' ');

    // Build fill path (closes to bottom)
    const fillPath = `${linePath} L ${points[points.length - 1].x},${height - padding} L ${points[0].x},${height - padding} Z`;

    return {
      line: linePath,
      fill: fillPath,
      lastPoint: points[points.length - 1],
    };
  }, [data, width, height, strokeWidth]);

  if (!data || data.length < 2) {
    return <div style={{ width, height }} className={className} />;
  }

  const gradientId = `sparkline-gradient-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <svg
      width={width}
      height={height}
      className={className}
      viewBox={`0 0 ${width} ${height}`}
    >
      {/* Gradient definition for fill */}
      {showFill && (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
      )}

      {/* Fill area */}
      {showFill && path.fill && (
        <path d={path.fill} fill={`url(#${gradientId})`} />
      )}

      {/* Line */}
      <path
        d={path.line}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Last point dot */}
      {showLastDot && path.lastPoint && (
        <circle
          cx={path.lastPoint.x}
          cy={path.lastPoint.y}
          r={strokeWidth + 1}
          fill={color}
        />
      )}
    </svg>
  );
};

export default Sparkline;
